import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {debounceTime, distinctUntilChanged, finalize, map, tap} from 'rxjs/operators';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {
  IChatHistory,
  IConversation,
  IConversationAuditEvent,
  IFunctionCallLog
} from "../../../models/conversation.model";
import {MasterThreadService} from "../../../services/objects/master-thread.service";
import {MaterialModule} from "../../../material.module";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {TabbedPageComponent} from "../../objects/tabbed-page/tabbed-page.component";
import {DatePipe, DOCUMENT, NgClass, NgTemplateOutlet} from "@angular/common";
import {MatSort} from '@angular/material/sort';
import {MatDialog} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ConversationPreviewService} from "../conversation-preview.service";
import {ChatbotsService} from "../../../services/objects/chatbots.service";
import {IChatbotOptions} from "../../../models/chatbot-options.model";
import {AssistantsService} from "../../../services/objects/assistants.service";
import {IAssistantOptions} from "../../../models/assistant-options.model";
import {ConversationTranscriptRendererComponent} from "../shared/conversation-transcript-renderer.component";
import {ClientRetentionService} from "../../../services/objects/client-retention.service";
import {ConversationSettingsComponent} from "../settings/conversation-settings.component";
import {IClientRetentionSettings} from "../../../models/client-retention-settings.model";
import {ClientService} from '../../../services/auth/client.service';
import {ConversationDownload, ConversationExportService} from '../conversation-export.service';

interface FunctionInputRow {
  label: string;
  value: string;
}

@Component({
  selector: 'app-conversations',
  templateUrl: './conversations.component.html',
  styleUrls: ['./conversations.component.scss'],
    imports: [
      MaterialModule,
      ReactiveFormsModule,
      DatePipe,
      NgClass,
      NgTemplateOutlet,
      ConversationTranscriptRendererComponent,
      ConversationSettingsComponent
    ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden', display: 'none' })),
      state('expanded', style({ height: '*', visibility: 'visible', display: 'block' })),
      transition('expanded <=> collapsed', animate('300ms ease-in-out')),
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationsComponent extends TabbedPageComponent implements OnInit, OnDestroy{
  private masterThreadService = inject(MasterThreadService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private conversationPreviewService = inject(ConversationPreviewService);
  private chatbotsService = inject(ChatbotsService);
  private assistantsService = inject(AssistantsService);
  private clientRetentionService = inject(ClientRetentionService);
  private document = inject<Document>(DOCUMENT);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly clientService = inject(ClientService);
  private readonly conversationExport = inject(ConversationExportService);
  private loadedClientId: string | null = null;
  private loadedDateFilter: string | null = null;
  exportDownloads: {csv?: ConversationDownload; json?: ConversationDownload} = {};

  readonly uncategorizedLabel = 'Uncategorized';
  displayedColumns: string[] = ['expand', 'numberOfChats', 'pageTitle', 'chatbot', 'category', 'feedback', 'created'];
  dataSource = new MatTableDataSource<IConversation>();
  searchControl = new FormControl('');
  chatbotControl = new FormControl('all');
  feedbackControl = new FormControl('all');
  categoryControl = new FormControl('all');
  chatbotOptions: Array<{id: string; name: string}> = [];
  categoryOptions: Array<{value: string; viewValue: string}> = [{value: 'all', viewValue: 'All categories'}];
  feedbackOptions = [
    {value: 'all', viewValue: 'All feedback'},
    {value: 'any', viewValue: 'Any feedback'},
    {value: 'up', viewValue: 'Thumbs up'},
    {value: 'down', viewValue: 'Thumbs down'}
  ];
  chatbotNameById: Record<string, string> = {};
  chatbotById: Record<string, IChatbotOptions> = {};
  assistantNameById: Record<string, string> = {};
  conversationSettings: IClientRetentionSettings | null = null;
  private baseConversations: IConversation[] = [];
  private configuredConversationCategories: string[] = [];

    // Tag filtering properties
  availableTags: string[] = [];
    displayedTags: string[] = []; // First 12 tags to display
    hiddenTags: string[] = []; // Remaining tags for dropdown
    hasMoreTags: boolean = false;
  selectedTag: string | null = null;
  tagCounts: Record<string, number> = {};
  allTagCount = 0;

    // Date filter properties
    dateFilterEnabled: boolean = true;
    dateRangeControl = new FormControl('7days');
    customStartDateControl = new FormControl<Date | null>(null);
    customEndDateControl = new FormControl<Date | null>(null);
    private _dateRangeSelectOpen = false;
    get dateRangeSelectOpen(): boolean {
        return this._dateRangeSelectOpen;
    }

    set dateRangeSelectOpen(value: boolean) {
        const isOpen = !!value;
        if (this._dateRangeSelectOpen === isOpen) {
            return;
        }

        this._dateRangeSelectOpen = isOpen;
        this.changeDetectorRef.markForCheck();
    }

    dateRangeOptions = [
        {value: '7days', viewValue: 'Last 7 days'},
        {value: '30days', viewValue: 'Last 30 days'},
        {value: 'custom', viewValue: 'Custom dates'},
        {value: 'all', viewValue: 'All time'}
    ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  expandedElement: any | null = null;
  deletingThreadId: string | null = null;
  isLoadingConversations = false;
  private readonly conversationQueryParamName = 'conversation';
  private pendingConversationId: string | null = null;

  ngOnInit(): void {

    super.OnInit();
    this.pendingConversationId = this.getConversationIdFromQuery();
    if (this.pendingConversationId && this.dateRangeControl.value !== 'all') {
      this.dateRangeControl.setValue('all', {emitEvent: false});
    }

    this.dataSource.filterPredicate = (data: IConversation, filter: string) => {
      const [searchTextRaw = '', tagTextRaw = '', chatbotTextRaw = '', feedbackTextRaw = '', categoryTextRaw = ''] = filter.split('|');
      const searchText = searchTextRaw.trim().toLowerCase();
      const tagText = tagTextRaw.trim().toLowerCase();
      const chatbotText = chatbotTextRaw.trim().toLowerCase();
      const feedbackText = feedbackTextRaw.trim().toLowerCase();
      const categoryText = categoryTextRaw.trim().toLowerCase();
      const email = (data.email || '').toLowerCase();
      const chatbotName = (this.chatbotNameById[data.shopAssistantId] || '').toLowerCase();
      const messagesWords = (data.messages || []).map(msg => msg.text || '').join(' ').toLowerCase();

      const searchMatch = email.includes(searchText) ||
          chatbotName.includes(searchText) ||
          messagesWords.includes(searchText);

      const tagMatch = tagText ? data.tags?.some(tag => (tag || '').toLowerCase() === tagText) : true;
      const chatbotMatch = chatbotText ? (data.shopAssistantId || '').toLowerCase() === chatbotText : true;
      const feedbackMatch = this.matchesFeedbackFilter(feedbackText, data.thumbUp, data.thumbDown);
      const categoryMatch = categoryText ? this.getCategoryDisplayName(data).toLowerCase() === categoryText : true;
      return searchMatch && tagMatch && chatbotMatch && feedbackMatch && categoryMatch;
    };

    this.searchControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.applyFilter())
    ).subscribe();

    this.chatbotControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.updateTagMetadataForChatbot();
          this.applyFilter();
        })
    ).subscribe();

    this.feedbackControl.valueChanges.pipe(
      distinctUntilChanged(),
      tap(() => this.applyFilter())
    ).subscribe();

    this.categoryControl.valueChanges.pipe(
      distinctUntilChanged(),
      tap(() => this.applyFilter())
    ).subscribe();

      // Subscribe to date range changes
      this.dateRangeControl.valueChanges.pipe(
          distinctUntilChanged(),
          tap(() => this.loadConversations())
      ).subscribe();

      this.customStartDateControl.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            if (this.isCustomDateRangeSelected()) {
              this.loadConversations();
            }
          })
      ).subscribe();

      this.customEndDateControl.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            if (this.isCustomDateRangeSelected()) {
              this.loadConversations();
            }
          })
      ).subscribe();

    this.route.queryParamMap.pipe(
      map(params => params.get(this.conversationQueryParamName)?.trim() || null),
      distinctUntilChanged(),
      tap(conversationId => {
        this.pendingConversationId = conversationId;
        if (!conversationId) {
          this.expandedElement = null;
          return;
        }

        this.openPendingConversation();
      })
    ).subscribe();

    this.loadConversations();
    this.loadConversationCategories();
    this.loadChatbots();
    this.loadAssistants();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    // Ensure date strings sort correctly by timestamp
    this.dataSource.sortingDataAccessor = (item: IConversation, property: string) => {
      switch (property) {
        case 'created':
          return new Date(item.created).getTime();
        case 'numberOfChats':
          return item.messages?.length || 0;
        case 'chatbot':
          return (this.getChatbotDisplayName(item) || '').toLowerCase();
        case 'category':
          return this.getCategoryDisplayName(item).toLowerCase();
        default:
          // @ts-ignore
          return (item as any)[property];
      }
    };
  }

  loadConversations(): void {
    const requestedClientId = this.clientService.client?.id || null;
    const requestedDateFilter = this.dateFilterSnapshot();
    this.loadedClientId = null;
    this.loadedDateFilter = null;
    this.isLoadingConversations = true;
    this.changeDetectorRef.markForCheck();
    this.masterThreadService.getMasterThreadsAsync()
      .pipe(finalize(() => {
        this.isLoadingConversations = false;
        this.changeDetectorRef.markForCheck();
      }))
      .subscribe(
        (data: IConversation[]) => {
            this.loadedClientId = requestedClientId;
            this.loadedDateFilter = requestedDateFilter;
            // Sort data by last modified date
            let sortedData = data.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());

            // Exclude conversations with zero messages
            sortedData = sortedData.filter(conv => (conv.messages?.length || 0) > 0);

            const selectedRange = this.getSelectedDateRange();
            if (selectedRange !== null) {
                sortedData = sortedData.filter(conv => {
                    const lastModified = new Date(conv.lastModified).getTime();

                    return (!selectedRange.start || lastModified >= selectedRange.start.getTime()) &&
                        (!selectedRange.end || lastModified <= selectedRange.end.getTime());
                });
            }

            this.baseConversations = sortedData;
            this.dataSource.data = sortedData;
            this.rebuildCategoryOptions(sortedData);
            this.updateTagMetadataForChatbot();
            this.applyFilter();
            this.openPendingConversation();
            this.changeDetectorRef.markForCheck();
        },
        (error) => console.error('Error fetching master threads', error)
    );
  }

  private loadConversationCategories(): void {
    this.clientRetentionService.getAsync().subscribe({
      next: (settings) => {
        this.conversationSettings = settings;
        this.configuredConversationCategories = settings.conversationCategories ?? [];
        this.rebuildCategoryOptions(this.baseConversations);
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => console.error('Error loading conversation categories', error)
    });
  }

  get canExportConversations(): boolean {
    return !this.isLoadingConversations && !!this.loadedClientId &&
      this.loadedClientId === this.clientService.client?.id &&
      this.loadedDateFilter === this.dateFilterSnapshot() && this.dataSource.filteredData.length > 0;
  }

  prepareConversationExports(): void {
    // Apply pending search input immediately, without waiting for its debounce timer.
    this.applyFilter();
    this.releaseConversationExports();
    this.exportDownloads = {};
    if (!this.canExportConversations) return;
    const conversations = this.dataSource.filteredData;
    try {
      this.exportDownloads.csv = this.conversationExport.createDownload(conversations, this.loadedClientId!, this.chatbotNameById, 'csv');
      this.exportDownloads.json = this.conversationExport.createDownload(conversations, this.loadedClientId!, this.chatbotNameById, 'json');
    } catch {
      this.releaseConversationExports();
      this.exportDownloads = {};
      this.snackBar.open('Export failed. Reload conversations for the selected client and try again.', 'Close', {duration: 6000});
    }
  }

  releaseConversationExports(): void {
    for (const download of Object.values(this.exportDownloads)) this.conversationExport.release(download);
  }

  ngOnDestroy(): void {
    this.releaseConversationExports();
  }

  private dateFilterSnapshot(): string {
    return JSON.stringify(this.isCustomDateRangeSelected()
      ? [this.dateRangeControl.value, this.customStartDateControl.value, this.customEndDateControl.value]
      : [this.dateRangeControl.value]);
  }

  isCustomDateRangeSelected(): boolean {
    return this.dateRangeControl.value === 'custom';
  }

  showCustomDateRangeControls(): boolean {
    return this.isCustomDateRangeSelected() && !this.dateRangeSelectOpen;
  }

  private getSelectedDateRange(): {start?: Date; end?: Date} | null {
    const dateRange = this.dateRangeControl.value;

    if (dateRange === 'all') {
      return null;
    }

    if (dateRange === 'custom') {
      return this.getCustomDateRange();
    }

    const cutoffDate = new Date();

    if (dateRange === '30days') {
      cutoffDate.setDate(cutoffDate.getDate() - 30);
      return {start: cutoffDate};
    }

    cutoffDate.setDate(cutoffDate.getDate() - 7);
    return {start: cutoffDate};
  }

  private getCustomDateRange(): {start?: Date; end?: Date} | null {
    const start = this.toStartOfDay(this.customStartDateControl.value);
    const end = this.toEndOfDay(this.customEndDateControl.value);

    if (!start && !end) {
      return null;
    }

    if (start && end && start.getTime() > end.getTime()) {
      return {start: this.toStartOfDay(end), end: this.toEndOfDay(start)};
    }

    return {
      ...(start ? {start} : {}),
      ...(end ? {end} : {})
    };
  }

  private toStartOfDay(value: Date | string | null | undefined): Date | undefined {
    const date = this.toValidDate(value);
    if (!date) {
      return undefined;
    }

    date.setHours(0, 0, 0, 0);
    return date;
  }

  private toEndOfDay(value: Date | string | null | undefined): Date | undefined {
    const date = this.toValidDate(value);
    if (!date) {
      return undefined;
    }

    date.setHours(23, 59, 59, 999);
    return date;
  }

  private toValidDate(value: Date | string | null | undefined): Date | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? new Date(value) : new Date(value);

    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  applyFilter(): void {
    const searchValue = this.searchControl.value?.trim().toLowerCase() || '';
    const tagValue = this.selectedTag?.trim().toLowerCase() || '';
    const chatbotValue = this.getChatbotFilterValue();
    const feedbackValue = this.getFeedbackFilterValue();
    const categoryValue = this.getCategoryFilterValue();
    this.dataSource.filter = `${searchValue}|${tagValue}|${chatbotValue}|${feedbackValue}|${categoryValue}`;

  }

  // Called when a tag chip is clicked (null clears the filter)
  applyTagFilter(tag: string | null): void {
    this.selectedTag = tag;
    this.applyFilter();
  }

  toggleExpand(row: IConversation) {
    const nextConversationId = row.id === this.expandedElement ? null : row.id;
    this.expandedElement = nextConversationId;
    this.decorateConversationUrl(nextConversationId);
  }

  viewDetails(thread: IConversation): void {
    this.router.navigate(['/ai/conversations', thread.id]);
  }

  approveThread(thread: IConversation): void {
    this.masterThreadService.approveMasterThreadAsync(thread.id, thread).subscribe(
        () => {
          thread.approved = true;
          this.updateDataSource(thread);
        },
        (error) => {
          console.error('Error approving thread', error);
        }
    );
  }

  getTagCount(tag: string | null): number {
    if (!tag) {
      return this.allTagCount;
    }

    return this.tagCounts[tag] ?? 0;
  }

    getTagDisplayLabel(tag: string | null): string {
        const count = this.getTagCount(tag);
        const baseLabel = tag ?? 'All Tags';
        return `${baseLabel} (${count})`;
    }

  getTagAriaLabel(tag: string | null): string {
    const count = this.getTagCount(tag);
    const pluralised = count === 1 ? 'conversation' : 'conversations';

    if (!tag) {
      return `All tags (${count} ${pluralised})`;
    }

    return `${tag} (${count} ${pluralised})`;
  }

  declineThread(thread: IConversation): void {
    this.masterThreadService.declineMasterThreadAsync(thread.id, thread).subscribe(
        () => {
          thread.dismissed = true;
          this.updateDataSource(thread);
        },
        (error) => {
          console.error('Error declining thread', error);
          // Optionally, provide error feedback
        }
    );
  }

  deleteThread(thread: IConversation): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete conversation',
        message: 'Are you sure you want to delete this conversation?',
        info: 'This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.deletingThreadId = thread.id;
      this.masterThreadService.deleteMasterThreadAsync(thread.id)
        .pipe(finalize(() => this.deletingThreadId = null))
        .subscribe({
          next: () => {
            this.removeThreadFromData(thread.id);
            this.snackBar.open('Conversation deleted', undefined, { duration: 2500 });
          },
          error: (error) => {
            console.error('Error deleting thread', error);
            this.snackBar.open('Failed to delete conversation', undefined, { duration: 3000 });
          }
        });
    });
  }

  private updateDataSource(updatedThread: IConversation): void {
    const index = this.baseConversations.findIndex(t => t?.id === updatedThread.id);
    if (index !== -1) {
      const updatedData = [...this.baseConversations];
      updatedData[index] = updatedThread;
      this.baseConversations = updatedData;
      this.dataSource.data = updatedData;
      this.rebuildCategoryOptions(updatedData);
      this.updateTagMetadataForChatbot();
      this.applyFilter();
    }
  }

  private removeThreadFromData(threadId: string): void {
    const updatedData = this.baseConversations.filter(t => t?.id !== threadId);
    this.baseConversations = updatedData;
    this.dataSource.data = updatedData;
    this.rebuildCategoryOptions(updatedData);
    this.updateTagMetadataForChatbot();
    this.applyFilter();
  }

  private rebuildTagMetadata(conversations: IConversation[]): void {
    const tagCounter: Record<string, number> = {};
    const uniqueTags = new Set<string>();

    conversations.forEach(conv => {
      (conv.tags || []).forEach(tag => {
        const normalizedTag = tag?.trim();
        if (!normalizedTag) {
          return;
        }

        uniqueTags.add(normalizedTag);
        tagCounter[normalizedTag] = (tagCounter[normalizedTag] || 0) + 1;
      });
    });

    this.tagCounts = tagCounter;
    this.availableTags = Array.from(uniqueTags);
    this.allTagCount = conversations.length;

    const maxDisplayedTags = 12;
    if (this.availableTags.length > maxDisplayedTags) {
      this.displayedTags = this.availableTags.slice(0, maxDisplayedTags);
      this.hiddenTags = this.availableTags.slice(maxDisplayedTags);
      this.hasMoreTags = true;
    } else {
      this.displayedTags = this.availableTags;
      this.hiddenTags = [];
      this.hasMoreTags = false;
    }

    if (this.selectedTag && !this.tagCounts[this.selectedTag]) {
      this.selectedTag = null;
    }
  }

  getLastUserMessage(element: IConversation): IChatHistory | undefined {
    const userMessages = element.messages.filter(msg => msg.user);
    return userMessages[userMessages.length - 1];
  }

  getCleanUrl(url?: string): string {
    if (!url) {
      return '';
    }

    const queryIndex = url.indexOf('?');
    return queryIndex > -1 ? url.slice(0, queryIndex) : url;
  }

  getMessageCount(thread: IConversation, type: 'user' | 'assistant' | 'human' | 'all' = 'all'): number {
    const messages = thread.messages || [];
    switch (type) {
      case 'user':
        return messages.filter(message => message.user).length;
      case 'assistant':
        return messages.filter(message => !message.user && !message.human).length;
      case 'human':
        return messages.filter(message => !!message.human).length;
      default:
        return messages.length;
    }
  }

  getFunctionCallCount(thread: IConversation): number {
    return (thread.messages || []).reduce((count, message) => count + (message.functionsCalled?.length || 0), 0);
  }

  getUiUpdateCount(thread: IConversation): number {
    return (thread.messages || []).reduce((count, message) => count + (message.uiUpdates?.length || 0), 0);
  }

  hasAnswerProductionDetails(thread: IConversation): boolean {
    return this.getFunctionCallCount(thread) > 0 ||
      this.getUiUpdateCount(thread) > 0 ||
      this.getModelReferenceEvents(thread).length > 0 ||
      (thread.messages || []).some(message => !message.user && !!message.assistantSelectionReason);
  }

  getFunctionInputRows(thread: IConversation, functionCall: IFunctionCallLog): FunctionInputRow[] {
    const rawArguments = this.getFunctionArguments(thread, functionCall)?.trim();
    if (!rawArguments) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawArguments) as unknown;
      return this.toFunctionInputRows(parsed);
    } catch {
      return [{label: 'Input', value: this.formatFunctionInputValue(rawArguments)}];
    }
  }

  getAuditEvents(thread: IConversation): IConversationAuditEvent[] {
    return thread.auditEvents || [];
  }

  getAuditEventsByType(thread: IConversation, eventType: string): IConversationAuditEvent[] {
    return this.getAuditEvents(thread).filter(event => event.eventType === eventType);
  }

  getModelReferenceEvents(thread: IConversation): IConversationAuditEvent[] {
    return this.getAuditEventsByType(thread, 'model_reference').filter(event => !!event.reference);
  }

  private getFunctionArguments(thread: IConversation, functionCall: IFunctionCallLog): string | undefined {
    if (functionCall.argumentsPreview) {
      return functionCall.argumentsPreview;
    }

    const auditEvent = this.getAuditEventsByType(thread, 'function_call')
      .find(event => {
        if (functionCall.callId && event.callId === functionCall.callId) {
          return true;
        }

        return event.name === functionCall.name;
      });

    return auditEvent?.argumentsJson;
  }

  private toFunctionInputRows(value: unknown): FunctionInputRow[] {
    if (!this.isRecord(value)) {
      return [{label: 'Input', value: this.formatFunctionInputValue(value)}];
    }

    const rows = Object.entries(value)
      .filter(([key, entryValue]) => this.shouldShowFunctionInput(key, entryValue))
      .map(([key, entryValue]) => ({
        label: this.humanizeKey(key),
        value: this.formatFunctionInputValue(entryValue)
      }));

    return rows.length ? rows : [{label: 'Input', value: this.formatFunctionInputValue(value)}];
  }

  private shouldShowFunctionInput(key: string, value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    const lowerKey = key.toLowerCase();
    const hiddenTechnicalKeys = new Set([
      'id',
      'conversationid',
      'providerconversationid',
      'providerresponseid',
      'messageid',
      'clientmessageid',
      'callid',
      'clientid',
      'chatbotid'
    ]);

    return !hiddenTechnicalKeys.has(lowerKey);
  }

  private humanizeKey(key: string): string {
    const withSpaces = key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    return withSpaces
      ? withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
      : 'Input';
  }

  private formatFunctionInputValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'string') {
      return this.truncateFunctionInput(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      const formatted = value
        .slice(0, 5)
        .map(item => this.formatFunctionInputValue(item))
        .filter(item => item && item !== '—');
      const suffix = value.length > 5 ? ` +${value.length - 5} more` : '';
      return this.truncateFunctionInput(`${formatted.join(', ')}${suffix}`);
    }

    if (this.isRecord(value)) {
      const formatted = Object.entries(value)
        .filter(([key, entryValue]) => this.shouldShowFunctionInput(key, entryValue))
        .slice(0, 6)
        .map(([key, entryValue]) => `${this.humanizeKey(key)}: ${this.formatFunctionInputValue(entryValue)}`);
      return this.truncateFunctionInput(formatted.join('; '));
    }

    return this.truncateFunctionInput(String(value));
  }

  private truncateFunctionInput(value: string): string {
    const normalized = value.replace(/\s+/g, ' ').trim();
    const maxLength = 400;
    return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}…` : normalized;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  getDurationLabel(thread: IConversation): string {
    const created = new Date(thread.created).getTime();
    const modified = new Date(thread.lastModified).getTime();
    if (Number.isNaN(created) || Number.isNaN(modified) || modified < created) {
      return '—';
    }

    const minutes = Math.round((modified - created) / 60000);
    if (minutes < 1) {
      return '< 1 min';
    }
    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours = Math.round(minutes / 60);
    return `${hours} h`;
  }

  getFeedbackLabel(thread: IConversation): string {
    if (thread.thumbUp) {
      return 'Thumbs up';
    }
    if (thread.thumbDown) {
      return 'Thumbs down';
    }
    return 'No feedback';
  }

  getReferenceLabel(event: IConversationAuditEvent): string {
    const reference = event.reference;
    if (!reference) {
      return '';
    }

    return reference.title || reference.fileName || reference.url || reference.query || reference.fileId || reference.source || 'Reference';
  }

  getAssistantDisplayName(assistantId?: string): string {
    if (!assistantId) {
      return '';
    }

    return this.assistantNameById[assistantId] || assistantId;
  }

  getAssistantSelectionTooltip(message: { assistantSelectionReason?: string; assistantId?: string }): string {
    const reason = message.assistantSelectionReason?.trim();
    if (reason) {
      return reason;
    }

    return message.assistantId ? 'Assistant selected for this message.' : '';
  }

  getChatbotDisplayName(element: IConversation): string {
    const chatbotName = this.chatbotNameById[element.shopAssistantId];
    return chatbotName || element.shopAssistantId || '—';
  }

  getCategoryDisplayName(element: IConversation): string {
    const category = element.category?.trim();
    return category || this.uncategorizedLabel;
  }

  getChatbotOptions(element: IConversation): IChatbotOptions | null {
    return this.chatbotById[element.shopAssistantId] || null;
  }

  isConversationExpanded(thread: IConversation): boolean {
    return this.expandedElement === thread.id;
  }

  getVisibleConversations(): IConversation[] {
    const conversations = this.dataSource.filteredData ?? this.dataSource.data ?? [];
    const defaultPageSize = this.paginator?.pageSizeOptions?.[0] || 10;
    const pageSize = this.paginator?.pageSize || defaultPageSize;
    const pageIndex = this.paginator?.pageIndex || 0;
    const start = pageIndex * pageSize;

    return conversations.slice(start, start + pageSize);
  }

  getErrorMessages() {
    return this.dataSource.data?.map(x => x.errorMessages);
  }

  previewInWidget(thread: IConversation): void {
    if (!thread?.clientId || !thread?.shopAssistantId) {
      return;
    }

    this.conversationPreviewService.open({
      conversationId: thread.id,
      clientId: thread.clientId,
      chatbotId: thread.shopAssistantId,
      title: thread.email || thread.hubSpotId || 'Vis i chatbot'
    });
  }

  copyConversationLink(thread: IConversation): void {
    const url = this.getConversationLink(thread);
    const clipboard = this.document.defaultView?.navigator?.clipboard;

    if (clipboard?.writeText) {
      clipboard.writeText(url)
        .then(() => this.snackBar.open('Conversation link copied', undefined, { duration: 2500 }))
        .catch(() => this.snackBar.open(url, 'Dismiss', { duration: 5000 }));
      return;
    }

    this.snackBar.open(url, 'Dismiss', { duration: 5000 });
  }

  private loadChatbots(): void {
    this.chatbotsService.getAllAsync().subscribe({
      next: (chatbots: IChatbotOptions[]) => {
        this.chatbotById = (chatbots || [])
          .filter(bot => !!bot?.id)
          .reduce<Record<string, IChatbotOptions>>((acc, bot) => {
            acc[bot.id] = bot;
            return acc;
          }, {});

        const options = (chatbots || [])
          .filter(bot => !!bot?.id)
          .map(bot => ({
            id: bot.id,
            name: bot.name?.trim() || bot.id
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        this.chatbotOptions = options;
        this.chatbotNameById = options.reduce<Record<string, string>>((acc, bot) => {
          acc[bot.id] = bot.name;
          return acc;
        }, {});
        this.applyFilter();
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => console.error('Error fetching chatbots', error)
    });
  }

  private loadAssistants(): void {
    this.assistantsService.getAllAsync().subscribe({
      next: (assistants: IAssistantOptions[]) => {
        this.assistantNameById = (assistants || []).reduce<Record<string, string>>((acc, assistant) => {
          const label = assistant.name?.trim() || assistant.assistantId || assistant.id;
          if (assistant.id) acc[assistant.id] = label;
          if (assistant.assistantId) acc[assistant.assistantId] = label;
          return acc;
        }, {});
      },
      error: (error) => console.error('Error fetching assistants', error)
    });
  }

  private getChatbotFilterValue(): string {
    const value = this.chatbotControl.value?.trim() || '';
    return value === 'all' ? '' : value;
  }

  private getFeedbackFilterValue(): string {
    const value = this.feedbackControl.value?.trim().toLowerCase() || '';
    return value === 'all' ? '' : value;
  }

  private getCategoryFilterValue(): string {
    const value = this.categoryControl.value?.trim() || '';
    return value === 'all' ? '' : value;
  }

  private matchesFeedbackFilter(filterValue: string, thumbUp?: boolean, thumbDown?: boolean): boolean {
    if (!filterValue) {
      return true;
    }

    const hasThumbUp = !!thumbUp;
    const hasThumbDown = !!thumbDown;

    switch (filterValue) {
      case 'up':
        return hasThumbUp;
      case 'down':
        return hasThumbDown;
      case 'any':
        return hasThumbUp || hasThumbDown;
      default:
        return true;
    }
  }

  private rebuildCategoryOptions(conversations: IConversation[]): void {
    const actualCategories = Array.from(new Set(
      conversations.map(conversation => this.getCategoryDisplayName(conversation))
    ));
    const configuredCategories = (this.configuredConversationCategories || [])
      .filter(category => !!category && category.toLowerCase() !== this.uncategorizedLabel.toLowerCase());
    const extraActualCategories = actualCategories
      .filter(category =>
        category !== this.uncategorizedLabel &&
        !configuredCategories.some(configured => configured.toLowerCase() === category.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    const uniqueCategories = [
      ...configuredCategories,
      ...extraActualCategories,
      ...(actualCategories.includes(this.uncategorizedLabel) || configuredCategories.length > 0 || extraActualCategories.length > 0
        ? [this.uncategorizedLabel]
        : [])
    ];

    this.categoryOptions = [
      {value: 'all', viewValue: 'All categories'},
      ...uniqueCategories.map(category => ({value: category, viewValue: category}))
    ];

    const selected = this.categoryControl.value;
    if (selected && selected !== 'all' && !uniqueCategories.includes(selected)) {
      this.categoryControl.setValue('all', {emitEvent: false});
    }
  }

  onConversationSettingsSaved(settings: IClientRetentionSettings): void {
    this.conversationSettings = settings;
    this.configuredConversationCategories = settings.conversationCategories ?? [];
    this.rebuildCategoryOptions(this.baseConversations);
    this.changeDetectorRef.markForCheck();
  }

  private updateTagMetadataForChatbot(): void {
    const chatbotValue = this.getChatbotFilterValue();
    const scopedConversations = chatbotValue
      ? this.baseConversations.filter(conv => (conv.shopAssistantId || '').toLowerCase() === chatbotValue.toLowerCase())
      : this.baseConversations;
    this.rebuildTagMetadata(scopedConversations);
  }

  private getConversationIdFromQuery(): string | null {
    return this.route.snapshot.queryParamMap.get(this.conversationQueryParamName)?.trim() || null;
  }

  private decorateConversationUrl(conversationId: string | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[this.conversationQueryParamName]: conversationId},
      queryParamsHandling: 'merge'
    });
  }

  private openPendingConversation(): void {
    if (!this.pendingConversationId || this.baseConversations.length === 0) {
      return;
    }

    const conversation = this.baseConversations.find(item => item.id === this.pendingConversationId);
    if (!conversation) {
      return;
    }

    if (!this.dataSource.filteredData.some(item => item.id === conversation.id)) {
      this.clearFiltersForConversationLink();
    }

    this.expandedElement = conversation.id;
    this.movePaginatorToConversation(conversation.id);
  }

  private clearFiltersForConversationLink(): void {
    this.searchControl.setValue('', {emitEvent: false});
    this.chatbotControl.setValue('all', {emitEvent: false});
    this.feedbackControl.setValue('all', {emitEvent: false});
    this.selectedTag = null;
    this.updateTagMetadataForChatbot();
    this.applyFilter();
  }

  private movePaginatorToConversation(conversationId: string): void {
    if (!this.paginator) {
      return;
    }

    const rowIndex = this.dataSource.filteredData.findIndex(item => item.id === conversationId);
    if (rowIndex < 0) {
      return;
    }

    const pageSize = this.paginator.pageSize || this.paginator.pageSizeOptions?.[0] || 10;
    this.paginator.pageIndex = Math.floor(rowIndex / pageSize);
  }

  private getConversationLink(thread: IConversation): string {
    const tree = this.router.createUrlTree(['/ai/conversations'], {
      queryParams: {
        [this.queryParamName]: 1,
        [this.conversationQueryParamName]: thread.id
      }
    });
    const path = this.router.serializeUrl(tree);
    const origin = this.document.defaultView?.location.origin ?? '';
    return `${origin}${path}`;
  }
}
